import { Available } from "@/lib/suggest/available";
import { isValidHostname } from "@/lib/utils";
import {
  AppStatuses,
  AppTypeCheck,
  CompanyTypeCheck,
  EnvSchema,
  HostArchTypes,
  HostHealthSchema,
  HostStatuses,
  JournalTypes,
  ProjectSchema,
  ProviderStateCheck,
  ProviderTypes,
  ProvisionStatuses,
  ServiceStatuses,
  ServiceTypes,
  TaskArgsSchema,
  TaskStatuses,
  TaskTypes,
  UserReferralCheck,
  UserRoleCheck,
  UserSuggestionActions
} from "@/types";

import { z } from "zod";

export const HostSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  status: z.enum(HostStatuses).nullable().optional(),
  ipAddress: z.string().nullable(),
  privateKey: z.string().nullable().optional(),
  privateKeyId: z.string().nullable().optional(),
  username: z.string().nullable().optional(),
  password: z.string().nullable().optional(),
  osType: z.string().nullable().optional(),
  lastConnectedAt: z.coerce.date().nullable(),
  lastPreparedAt: z.coerce.date().nullable(),
  lastConfigAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  userId: z.string(),
  health: HostHealthSchema.nullable().optional(),
  hostnames: z.array(z.string()),
  tenantId: z.string(),
  swap: z.number().nullable().optional(),
  arch: z.enum(HostArchTypes).nullable().optional(),
  projectId: z.string().nullish(),
  env: EnvSchema.nullable(),
  backupSchedule: z.string().nullable().optional()
});

export const HostConsoleSessionResponseSchema = z.object({
  token: z.string(),
  expiresAt: z.number(),
  path: z.string()
});

export const GetHostResponseSchema = HostSchema.and(
  z.object({
    apps: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
          title: z.string().nullable().optional(),
          hostname: z.string().nullish(),
          status: z.enum(AppStatuses),
          mainPort: z.number().nullish()
        })
      )
      .nullish(),
    project: ProjectSchema.pick({
      id: true,
      name: true,
      description: true
    }).nullish()
  })
);

export const ServiceSchema = z.object({
  id: z.string(),
  type: z.enum(ServiceTypes),
  name: z.string().nullish(),
  customUrl: z.string().nullish(),
  status: z.enum(ServiceStatuses).nullable(),
  env: z.record(z.string(), z.string().or(z.number()).or(z.boolean())).nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  lastInstalledAt: z.coerce.date().nullish()
});

export const ServicesSchema = z.array(ServiceSchema);

export const HostPendingSchema = z.object({
  pending: z.boolean(),
  reasons: z.array(z.string())
});

export type HostPendingType = z.output<typeof HostPendingSchema>;

export const ListHostsResponseSchema = z
  .array(
    HostSchema.and(
      z.object({
        apps: z.array(
          z.object({
            id: z.string(),
            name: z.string(),
            title: z.string().nullable().optional(),
            status: z.enum(AppStatuses).nullable(),
            mainPort: z.number().nullish(),
            type: AppTypeCheck,
            templateId: z.string().optional()
          })
        ),
        services: ServicesSchema,
        project: ProjectSchema.pick({
          id: true,
          name: true,
          description: true
        }).nullish()
      })
    )
  )
  .describe("A list of hosts, include the apps on each host.");

export type HostSchemaType = z.output<typeof HostSchema>;

export const listQuerySchema = z.object({
  status: z
    .enum([...HostStatuses, "all"])
    .optional()
    .describe(
      "Filter by status. By default, archived hosts are not included. Specify 'all' to include all hosts."
    )
});

export const HostActionSchema = z.object({
  id: z.string().describe("The ID of the host")
});

export const HostDeprovisionActionSchema = z.object({
  deleteHost: z.boolean().optional().describe("Delete the host after deprovisioning")
});

export const ProviderActionSchema = z.object({
  provider: z.enum(ProviderTypes)
});

export const ProvisionSchema = z.object({
  id: z.string().optional(),
  provider: z.enum(ProviderTypes),
  providerRegion: z.string(),
  image: z.string(),
  providerType: z.string(),
  disk: z.number().int().positive().nullish().describe("The disk size in GB"),
  privateKeyId: z.string().nullish(),
  providerId: z.string().nullish(),
  provisionedAt: z.coerce.date().nullish(),
  status: z.enum(ProvisionStatuses).nullish()
});

export type ProvisionSchemaType = z.output<typeof ProvisionSchema>;

export const ProvisionCreateSchema = ProvisionSchema.omit({
  id: true,
  status: true,
  providerId: true,
  provisionedAt: true
});

export const TaskQueuedSchema = z
  .object({
    id: z.string(),
    type: z.enum(TaskTypes),
    status: z.enum(TaskStatuses),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    nextTaskId: z.string().nullish()
  })
  .describe(
    "A task that has been queued. You can track the task status with the get-task tool, or get the task journal with the get-task-journal tool."
  );

export const ServiceActionSchema = z.object({
  id: z.string().describe("The ID of the host to get the services"),
  serviceId: z.string().describe("The ID of the service to get the details or update")
});

export const HostCreateWildcardSchema = z.object({
  hostnames: z
    .array(
      z
        .string()
        .trim()
        .refine(value => isValidHostname(value), "Invalid hostname")
    )
    .optional()
    .describe("The existing hostnames."),
  ipAddress: z.string().optional().describe("The IP address to create the wildcard for.")
});

export const HostCreateWildcardOutputSchema = HostCreateWildcardSchema.extend({
  wildcard: z
    .string()
    .describe("The wildcard domain generated, in the form of *.<generated-id>.<domain>")
});

export const ProviderTypesSchema = z.array(z.enum(ProviderTypes));

export const TaskActionSchema = z.object({
  id: z.string().describe("The ID of the task")
});

export const TaskQueueSchema = z.object({
  force: z.boolean().optional()
});

export const TaskSchema = z.object({
  id: z.string(),
  type: z.enum(TaskTypes),
  status: z.enum(TaskStatuses),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  executing: z.boolean().nullish(),
  executionStartedAt: z.coerce.date().nullish(),
  executedAt: z.coerce.date().nullish(),
  result: z.string().optional().nullish(),
  nextTaskId: z.string().optional().nullish(),
  nextExecuteAt: z.coerce.date().optional().nullish(),
  interval: z.number().optional().nullish(),
  appId: z.string().optional().nullish(),
  hostId: z.string().optional().nullish(),
  userId: z.string(),
  tenantId: z.string(),
  args: TaskArgsSchema.optional().nullish()
});

export type TaskSchemaType = z.output<typeof TaskSchema>;

export const AppListQuerySchema = z
  .object({
    status: z.enum([...AppStatuses, "all"]).optional(),
    hostId: z.string().optional(),
    orderBy: z.string().optional().default("name"),
    orderDesc: z.string().optional()
  })
  .strict();

export const AppSchema = z.object({
  id: z.string(),
  name: z.string(),
  title: z.string().trim().max(60).nullable(),
  status: z.enum(AppStatuses),
  description: z.string().nullable(),
  type: AppTypeCheck,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  userId: z.string(),
  hostId: z.string().nullable(),
  tenantId: z.string(),
  hostname: z.string().nullable(),
  repositoryUrl: z.string().url().nullable(),
  sourcePath: z.string().nullable(),
  sourceBranch: z.string().nullable(),
  sourceBuildTag: z.string().nullable(),
  mainPort: z.number().nullable(),
  startScript: z.string().nullable(),
  startCmd: z.string().nullable(),
  buildScript: z.string().nullable(),
  buildCmd: z.string().nullable(),
  installCmd: z.string().nullable(),
  buildUrl: z.string().url().nullable(),
  buildSize: z.number().nullable(),
  buildToken: z.string().nullable(),
  lastBuildAt: z.coerce.date().nullable(),
  lastBuildTime: z.number().nullable(),
  lastDeployAt: z.coerce.date().nullable(),
  lastDeployTime: z.number().nullable(),
  dockerComposeFile: z.string().nullable(),
  preStartCmd: z.string().nullable(),
  postStartCmd: z.string().nullable(),
  buildIncludeFiles: z.string().nullable(),
  allowAccessFrom: z.string().nullable(),
  backupExcludeFiles: z.string().nullable(),
  backupCmd: z.string().nullable(),
  backupSchedule: z.string().nullable(),
  buildPath: z.string().nullable(),
  webPath: z.string().nullable(),
  githubInstallationId: z.number().nullable(),
  githubWebhookId: z.number().nullable(),
  deletedAt: z.coerce.date().nullable(),
  projectId: z.string().nullable(),
  env: z.record(z.string(), z.string().or(z.number()).or(z.boolean())).nullable()
});

export const AppUpdateSchema = AppSchema.omit({
  id: true,
  userId: true,
  tenantId: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  buildToken: true,
  buildUrl: true,
  buildSize: true,
  lastBuildAt: true,
  lastBuildTime: true,
  lastDeployAt: true,
  lastDeployTime: true,
  githubInstallationId: true,
  githubWebhookId: true
}).partial();

export type AppSchemaType = z.output<typeof AppSchema>;

export const AppWithHostSchema = AppSchema.and(
  z.object({
    host: HostSchema.pick({
      id: true,
      name: true,
      ipAddress: true,
      hostnames: true
    })
      .nullable()
      .optional(),
    project: ProjectSchema.pick({
      id: true,
      name: true,
      description: true
    }).nullish()
  })
);

export type AppWithHostSchemaType = z.output<typeof AppWithHostSchema>;

export const AppListResponseSchema = z.array(AppWithHostSchema);

export type AppListResponseType = z.output<typeof AppListResponseSchema>;

export const AppCreateSchema = AppUpdateSchema.omit({ status: true });

export const AppQuerySchema = z.object({ id: z.string().describe("The ID of the app") });

export const BackupQuerySchema = z.object({
  id: z.string().describe("The ID of the backup")
});

export const BackupDeletedSchema = z.object({
  id: z.string().describe("The ID of the deleted backup"),
  deleted: z.literal(true)
});

export const AppFindAvailablePortQuerySchema = z.object({
  id: z.string().optional().describe("The ID of the app"),
  hostId: z.string().describe("The ID of the host"),
  type: z.string().optional().describe("The type of the app"),
  port: z.coerce.number().optional().describe("The preferred starting port")
});

export const AppFindAvailablePortResponseSchema = z.object({
  port: z.number()
});

export const AppEnvStrategySchema = z
  .enum(["replace", "update"])
  .describe(
    "How to update environment variables when saving the app. 'replace' replaces all env vars with the provided ones (default). 'update' adds or changes only the provided env vars, leaving other existing vars intact (no deletion)."
  );

export const AppUpdateQuerySchema = AppQuerySchema.extend({
  env: AppEnvStrategySchema.optional().default("replace")
});

export type AppUpdateQueryType = z.output<typeof AppUpdateQuerySchema>;

export const AppEnvUpdateSchema = z.object({
  add: z
    .record(z.string(), z.union([z.string(), z.number(), z.boolean()]))
    .optional()
    .describe(
      "Env vars to add to the app. Upsert: each key is set to the given value whether or not it already exists. Functionally equivalent to 'update' — split for intent only."
    ),
  update: z
    .record(z.string(), z.union([z.string(), z.number(), z.boolean()]))
    .optional()
    .describe(
      "Env vars to update on the app. Upsert: each key is set to the given value whether or not it already exists. Functionally equivalent to 'add' — split for intent only."
    ),
  delete: z
    .array(z.string())
    .optional()
    .describe(
      "Keys of env vars to delete from the app. Keys not present are silently ignored."
    )
});

export type AppEnvUpdateType = z.output<typeof AppEnvUpdateSchema>;

export const AppBuildSchema = z.object({
  deploy: z.boolean().optional().describe("Deploy after build completes")
});

export const AppRemoveSchema = z.object({
  deleteApp: z.boolean().optional().describe("Delete the app after removal from the server")
});

export const AppSuggestQuerySchema = z.object({
  skipCache: z.boolean().optional().describe("Skip cache"),
  verbose: z.boolean().optional().describe("Verbose output")
});

export const AppSuggestBodySchema = AppUpdateSchema.extend({
  id: z.string().optional().describe("The ID of the app"),
  tenantId: z.string().optional().describe("The ID of the tenant")
});

export const AppSuggestionSchema = z.object({
  id: z.string().describe("The ID of the suggestion"),
  property: z.string().describe("The property to suggest"),
  value: z.string().or(z.number()).nullish().describe("The value to suggest"),
  values: z.record(z.string(), z.string()).nullish().describe("The values to suggest"),
  reason: z.string().describe("The reason for the suggestion")
});

export const AppSuggestionsSchema = z.array(AppSuggestionSchema);

export const LatestJournalMessageSchema = z.object({
  id: z.string(),
  type: z.enum(JournalTypes),
  message: z.string(),
  createdAt: z.coerce.date(),
  path: z.string().nullish(),
  data: HostHealthSchema.nullish(),
  hostId: z.string().nullish(),
  appId: z.string().nullish(),
  taskId: z.string().nullish(),
  tenantId: z.string(),
  hash: z.string().nullish(),
  task: z
    .object({
      id: z.string(),
      type: z.string(),
      status: z.enum(TaskStatuses)
    })
    .nullish(),
  app: z
    .object({
      id: z.string(),
      name: z.string(),
      title: z.string().nullable().optional(),
      status: z.enum(AppStatuses).nullish()
    })
    .nullish(),
  host: z
    .object({
      id: z.string(),
      name: z.string(),
      status: z.enum(HostStatuses).nullish()
    })
    .nullish()
});

export type LatestJournalMessageType = z.output<typeof LatestJournalMessageSchema>;

export const LatestJournalResponseSchema = z.object({
  page: z.number().nullish(),
  limit: z.number(),
  elapsed: z.number(),
  logs: z.array(LatestJournalMessageSchema)
});

export type LatestJournalResponseType = z.output<typeof LatestJournalResponseSchema>;

export const LatestJournalCountSchema = z.object({
  count: z.number(),
  elapsed: z.number()
});

export type LatestJournalCountType = z.output<typeof LatestJournalCountSchema>;

export const LatestJournalRequestSchema = z.object({
  page: z.coerce.number().default(1).describe("The page number to return"),
  limit: z.coerce.number().default(10).describe("The number of results to return"),
  sortBy: z.string().nullish().default("createdAt").describe("The field to sort by"),
  sortOrder: z
    .enum(["asc", "desc"])
    .default("desc")
    .optional()
    .describe("The order of the results"),
  taskId: z.string().optional().describe("The ID of the task to filter by"),
  type: z
    .string()
    .default("info,warn,error")
    .nullish()
    .optional()
    .describe(
      "The types of logs records to show. A comma separated list of info, warn, error, health, log, ai"
    ),
  message: z.string().optional().describe("The message contents to filter by"),
  createdAt: z
    .string()
    .nullish()
    .optional()
    .describe(
      "The date range to filter by. Comma separated list of two ISO 8601 dates or relative values (10m), or one date / relative value for the start date"
    ),
  path: z
    .string()
    .nullish()
    .describe(
      "Comma separated list of paths to filter by. Path is a file name or other identifier for the log record of specific type"
    ),
  hostId: z.string().nullish(),
  appId: z.string().nullish(),
  taskType: z.string().nullish().describe("Comma separated list of task types to filter by")
});

export const TemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  logo: z.string().optional(),
  intro: z.string(),
  description: z.string().optional(),
  tags: z.array(z.string()).optional(),
  experimental: z.boolean().optional(),
  introVideoUrl: z.string().optional(),
  deployTime: z.string().optional(),
  provider: z.string().optional(),
  requirements: z
    .object({
      memory: z.number().optional(),
      cpu: z.number().optional(),
      storage: z.number().optional(),
      gpu: z
        .object({
          model: z.string(),
          count: z.number()
        })
        .optional()
    })
    .optional(),
  services: z.array(z.string()).optional(),
  postLaunchNote: z.string().optional(),
  source: z.string().optional(),
  demoUrl: z.string().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  app: z.object({
    type: z.string(),
    dockerComposeFile: z.string().optional(),
    repositoryUrl: z.string(),
    sourcePath: z.string().optional(),
    sourceBranch: z.string().optional(),
    startScript: z.string().optional(),
    startCmd: z.string().optional(),
    buildScript: z.string().optional(),
    buildCmd: z.string().optional(),
    installCmd: z.string().optional(),
    preStartCmd: z.string().optional(),
    postStartCmd: z.string().optional(),
    buildIncludeFiles: z.string().optional(),
    allowAccessFrom: z.string().optional(),
    backupExcludeFiles: z.string().optional(),
    backupCmd: z.string().optional(),
    buildPath: z.string().optional(),
    mainPort: z.number().optional(),
    env: EnvSchema.nullish(),
    files: z
      .array(
        z.object({
          path: z.string(),
          content: z.string().optional()
        })
      )
      .optional()
  })
});

export const ListTemplatesSchema = z.array(TemplateSchema);

export const GetTemplateQuerySchema = z.object({
  id: z.string().describe("The ID of the template"),
  skipCache: z.coerce.boolean().optional()
});

export const ListTemplatesQuerySchema = z.object({
  sort: z.enum(["popular", "newest"]).optional(),
  limit: z.coerce.number().optional(),
  tag: z.string().optional(),
  skipCache: z.coerce.boolean().optional(),
  search: z.string().optional()
});

export const PrivateKeySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum(["ssh", "apiKey", "oauth"]),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  userId: z.string(),
  tenantId: z.string(),
  privateKey: z.string().optional()
});

/** Dynamic client registration request (RFC 7591). */
export const OAuthRegisterSchema = z.object({
  client_name: z.string().trim().min(1),
  redirect_uris: z.array(z.string().trim().url()).min(1)
});

/**
 * Registered OAuth client, persisted in the cache under `oauth:<client_id>`.
 * `redirect_uris` is the allowlist that authorize requests are matched against.
 */
export const OAuthClientSchema = z.object({
  client_name: z.string(),
  redirect_uris: z.array(z.string().url())
});
export type OAuthClientSchemaType = z.output<typeof OAuthClientSchema>;

/** PKCE authorization request shared by the consent page and /api/oauth/authorize. */
export const OAuthAuthorizeSchema = z.object({
  client_id: z.string(),
  redirect_uri: z.string().url(),
  code_challenge: z.string(),
  code_challenge_method: z.enum(["S256"]),
  state: z.string().optional()
});
export type OAuthAuthorizeSchemaType = z.output<typeof OAuthAuthorizeSchema>;

export const OAuthAuthorizeResponseSchema = z.object({
  redirect_uri: z.string()
});

export const SSHKeyResponseSchema = PrivateKeySchema.omit({ privateKey: true }).and(
  z.object({
    fingerprint: z.string(),
    publicKey: z.string(),
    user: z.object({
      email: z.string(),
      name: z.string()
    })
  })
);
export type SSHKeyResponseSchemaType = z.output<typeof SSHKeyResponseSchema>;

export const SSHKeyListResponseSchema = z.array(SSHKeyResponseSchema);
export type SSHKeyListResponseType = z.output<typeof SSHKeyListResponseSchema>;

export const SSHKeyQuerySchema = z.object({
  id: z.string().describe("The ID of the SSH key")
});

export const SSHKeyGetPublicKeyQuerySchema = z.object({
  id: z.string().describe("The ID of the SSH key"),
  comment: z.string().optional().describe("The comment to add to the public key")
});

export const SSHKeyCreateSchema = z.object({
  name: z.string().min(3, "SSH key name must be at least 3 characters"),
  privateKey: z.string().min(1, "SSH private key is required")
});

export const SSHKeyUpdateSchema = z.object({
  name: z.string().min(3, "SSH key name must be at least 3 characters").optional()
});

export const SSHKeyPublicKeyResponseSchema = z.object({
  publicKey: z.string(),
  fingerprint: z.string()
});

export const SSHKeyGenerateResponseSchema = z.object({
  publicKey: z.string(),
  sshPrivateKey: z.string(),
  sshPublicKey: z.string(),
  fingerprint: z.string()
});

export const SettingsUpdateTemplatesSchema = z.object({
  templates: z.string().nullish().describe("The user's custom templates, in YAML format")
});

export const SettingsUpdateGithubSchema = z.object({
  githubToken: z.string().nullish().describe("The user's GitHub personal access token"),
  githubUsername: z.string().nullish().describe("The user's GitHub username")
});

export const UserSuggestionReactionSchema = z.object({
  suggestionId: z.string().describe("The ID of the suggestion"),
  property: z.string().describe("The property name, for example 'sourcePath'"),
  name: z
    .string()
    .optional()
    .describe("Environment variable name, for example 'URL' if property is 'env'"),
  value: z.string().or(z.number()).or(z.boolean()).nullish().describe("The suggested value"),
  action: z.enum(UserSuggestionActions).describe("The action to take on the suggestion")
});

export const UserSuggestionSchema = z.object({
  id: z.string(),
  userId: z.string(),
  tenantId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  suggestionId: z.string(),
  property: z.string(),
  name: z.string().nullable(),
  action: z.enum(UserSuggestionActions)
});

export const GithubRepoQuerySchema = z.object({
  repositoryUrl: z.string().url().describe("The URL of the github repository"),
  appId: z.string().optional().describe("Optional existing app id"),
  sourcePath: z.string().optional().describe("The source path in the repository"),
  dockerComposeFile: z
    .string()
    .optional()
    .describe(
      "The docker compose file in the repository, to fetch, if project is known to be a docker compose project"
    ),
  skipCache: z.boolean().optional().describe("Skip cache, refetch repo again"),
  includes: z
    .array(z.string())
    .optional()
    .describe("Glob patterns to include in the repository snapshot")
});

export const GithubRepoSchema = z
  .object({
    availability: z
      .record(z.nativeEnum(Available), z.boolean())
      .describe(
        "The availability of the different build files, such as docker-compose.yml, package.json, etc."
      ),
    // Assoc array of file path to file content
    files: z
      .record(z.string(), z.string())
      .describe("The files in the repository, key is the file path, value is the file content")
  })
  .describe("A github repository snapshot");

// Github provider account
export const GithubProviderSchema = z.object({
  id: z.string(),
  type: z.string(),
  userId: z.string(),
  tenantId: z.string(),
  createdAt: z.coerce.date(),
  installationId: z.number().nullable(),
  /** Installation information for a GitHub app, one per account. */
  auth: z
    .object({
      id: z.number(),
      account: z.object({
        login: z.string(),
        id: z.number(),
        type: z.enum(["User", "Organization"])
      }),
      repository_selection: z.enum(["all", "selected"]).nullable()
    })
    .nullable()
});

export type GithubProvider = z.output<typeof GithubProviderSchema>;

export const GithubAccountsResponseSchema = z.array(GithubProviderSchema);

export const GithubRepositorySchema = z.object({
  id: z.number(),
  name: z.string(),
  private: z.boolean(),
  full_name: z.string(),
  html_url: z.string()
});

export type GithubRepository = z.output<typeof GithubRepositorySchema>;

export const GithubRepositoryResponseSchema = z.object({
  total: z.number(),
  repos: z.array(GithubRepositorySchema)
});

export const GithubRepositoryQuerySchema = z.object({
  id: z.string().describe("The provider ID")
});

export const OnboardingSchema = z.object({
  onboarding: z.number(),
  companyType: CompanyTypeCheck,
  userReferral: UserReferralCheck.describe("How the user heard about DollarDeploy"),
  name: z.string().trim().min(1),
  tenantName: z.string().trim().min(1),
  marketingOptIn: z.boolean(),
  tosAccepted: z.literal(true),
  privacyPolicyAccepted: z.literal(true)
});

export const OnboardingOptionalSchema = OnboardingSchema.partial();

/** A tenant the current user is a member of, for the tenant switcher. */
export const TenantMembershipSchema = z.object({
  tenantId: z.string(),
  name: z.string(),
  roles: z.array(UserRoleCheck),
  status: z.string().nullable().optional(),
  current: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  users: z.number(),
  apps: z.number(),
  hosts: z.number(),
  planId: z.string().nullish()
});

export const TenantMembershipListSchema = z.array(TenantMembershipSchema);

export const TenantSwitchRequestSchema = z.object({
  tenantId: z.string()
});

export const TenantCreateRequestSchema = z.object({
  name: z.string().trim().min(1)
});

export const TenantCreateResponseSchema = z.object({
  id: z.string()
});

/** `status` is the provider's own word; `state` is that word grouped into our five. */
export const ProvisionCheckSchema = z.object({
  status: z.string().optional(),
  state: ProviderStateCheck
});

export const AnnounceTypeSchema = z.enum(["changelog", "announcement", "urgent"]);

export const AnnounceSchema = z.object({
  type: AnnounceTypeSchema,
  title: z.string(),
  url: z.string(),
  slug: z.string(),
  version: z.string().nullish()
});

export type AnnounceSchemaType = z.output<typeof AnnounceSchema>;
